import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';

/**
 * Set and format landing pages (Asana 1219287567650303). Every endpoint reads
 * the nightly SeoPageAggregate snapshot; nothing is computed on request.
 * Public, no auth.
 */

/** One ranked row. `count` is want users, completed trades or have copies by list. */
export interface SeoCardRow {
  name: string;
  /** Lowercase Scryfall set code. */
  setCode: string;
  /** As stored (e.g. "BBD-249" on The List). */
  collectorNumber: string;
  /** The card-name slug used by /cards/{slug}/{set}/{number}. */
  slug: string;
  imageUrl: string | null;
  /** Points; null when the backend has no price. Never estimated. */
  pointsPrice: number | null;
  count: number;
}

export interface SeoSetListRow {
  setCode: string;
  name: string;
  releasedAt: string;
  /** Sitemap rule: not an excluded type and at least 5 English non-digital printings. */
  indexable: boolean;
}

/**
 * One /seo/formats item. The counts and preview arrive with the Format Snapshots
 * backend (Asana 1219902864982669); they are optional so the index renders
 * against an older backend without the strip.
 */
export interface SeoFormatListRow {
  formatKey: string;
  name: string;
  indexable: true;
  wantUsers?: number;
  tradeCount90d?: number;
  haveCopies?: number;
  /** The first three Most Wanted rows, basics excluded. */
  mostWantedPreview?: SeoCardRow[];
}

export interface SeoPageStats {
  wantUsers: number;
  tradeCount90d: number;
  /** SUM(quantity) of for-trade haves over the page's cards (Asana 1219902864982669). */
  haveCopies?: number;
}

export interface SeoSetPage {
  setCode: string;
  name: string;
  releasedAt: string;
  cardCount: number;
  indexable: boolean;
  computedAt: string;
  stats: SeoPageStats;
  mostWanted: SeoCardRow[];
  mostTraded: SeoCardRow[];
  mostAvailable: SeoCardRow[];
}

export interface SeoFormatPage {
  formatKey: string;
  name: string;
  indexable: true;
  computedAt: string;
  stats: SeoPageStats;
  mostWanted: SeoCardRow[];
  mostTraded: SeoCardRow[];
  mostAvailable: SeoCardRow[];
}

const AN_HOUR = 60 * 60;

export const publicSeoApi = createApi({
  reducerPath: 'publicSeoApi',
  baseQuery,
  tagTypes: ['SeoSets', 'SeoFormats'],
  endpoints: (builder) => ({
    /** Small and stable: cached for an hour so the card page's "More from {Set}" gate costs one request. */
    getSeoSets: builder.query<SeoSetListRow[], void>({
      query: () => ({ url: '/seo/sets', method: 'GET', requiresAuth: false }),
      providesTags: ['SeoSets'],
      keepUnusedDataFor: AN_HOUR,
    }),
    getSeoSet: builder.query<SeoSetPage, string>({
      query: (setCode) => ({ url: `/seo/sets/${setCode.toLowerCase()}`, method: 'GET', requiresAuth: false }),
      providesTags: (_r, _e, setCode) => [{ type: 'SeoSets', id: setCode.toLowerCase() }],
    }),
    getSeoFormats: builder.query<SeoFormatListRow[], void>({
      query: () => ({ url: '/seo/formats', method: 'GET', requiresAuth: false }),
      providesTags: ['SeoFormats'],
      keepUnusedDataFor: AN_HOUR,
    }),
    getSeoFormat: builder.query<SeoFormatPage, string>({
      query: (formatKey) => ({ url: `/seo/formats/${formatKey.toLowerCase()}`, method: 'GET', requiresAuth: false }),
      providesTags: (_r, _e, formatKey) => [{ type: 'SeoFormats', id: formatKey.toLowerCase() }],
    }),
  }),
});

export const { useGetSeoSetsQuery, useGetSeoSetQuery, useGetSeoFormatsQuery, useGetSeoFormatQuery } = publicSeoApi;
